/**
 * Assignment API service
 */

import { apiClient as api } from './api';
import type { Assignment, AssignmentCreate, AssignmentStats, BulkAssignmentCreate } from '../types/assignment';
import type { Lab } from '../types';

class AssignmentService {
  /**
   * Create a new assignment
   */
  async createAssignment(assignmentData: AssignmentCreate): Promise<Assignment> {
    return api.post('/assignments/', assignmentData);
  }

  /**
   * Get assignments with filtering
   */
  async getAssignments(filters?: {
    user_id?: number;
    lab_id?: string;
    is_active?: boolean;
    assigned_by_id?: number;
  }): Promise<Assignment[]> {
    const params = new URLSearchParams();
    
    if (filters?.user_id) params.append('user_id', filters.user_id.toString());
    if (filters?.lab_id) params.append('lab_id', filters.lab_id);
    if (filters?.is_active !== undefined) params.append('is_active', filters.is_active.toString());
    if (filters?.assigned_by_id) params.append('assigned_by_id', filters.assigned_by_id.toString());

    const response = await api.get(`/assignments/?${params.toString()}`);
    
    // Handle case where API client returns array directly vs wrapped response
    if (Array.isArray(response)) {
      return response;
    }
    
    return response.data;
  }

  /**
   * Get specific assignment details
   */
  async getAssignment(assignmentId: number): Promise<Assignment> {
    return api.get(`/assignments/${assignmentId}`);
  }

  /**
   * Update assignment status
   */
  async updateAssignment(assignmentId: number, updates: { is_active?: boolean }): Promise<Assignment> {
    return api.put(`/assignments/${assignmentId}`, updates);
  }

  /**
   * Remove assignment (mark as inactive)
   */
  async removeAssignment(assignmentId: number): Promise<void> {
    await api.delete(`/assignments/${assignmentId}`);
  }

  /**
   * Get labs assigned to a specific user
   */
  async getUserAssignedLabs(userId: number, includeCompleted = false): Promise<Lab[]> {
    const params = new URLSearchParams();
    if (includeCompleted) params.append('include_completed', 'true');

    const response = await api.get(`/assignments/users/${userId}/assigned-labs?${params.toString()}`);
    return Array.isArray(response) ? response : response.data;
  }

  /**
   * Create multiple assignments at once
   */
  async createBulkAssignments(bulkData: BulkAssignmentCreate): Promise<{
    created: number;
    skipped: number;
    errors: string[];
  }> {
    return api.post('/assignments/bulk', bulkData);
  }

  /**
   * Get assignment statistics
   */
  async getAssignmentStats(): Promise<AssignmentStats> {
    return api.get('/assignments/stats/overview');
  }

  /**
   * Check if a lab is assigned to the current user
   */
  async isLabAssignedToUser(userId: number, labId: string): Promise<boolean> {
    try {
      const assignments = await this.getAssignments({
        user_id: userId,
        lab_id: labId,
        is_active: true
      });
      return assignments.length > 0;
    } catch (error) {
      console.error('Error checking lab assignment:', error);
      return false;
    }
  }

  /**
   * Get assigned labs for current user (convenience method)
   */
  async getMyAssignedLabs(includeCompleted = false): Promise<Lab[]> {
    const url = includeCompleted 
      ? '/assignments/users/me/assigned-labs?include_completed=true'
      : '/assignments/users/me/assigned-labs';
    
    const response = await api.get(url);
    
    // Handle case where API client returns array directly vs wrapped response
    if (Array.isArray(response)) {
      return response;
    }
    
    return response.data;
  }
}

export const assignmentService = new AssignmentService();
